import { socketIOSettings } from '@config/socket.io';
import { API_ENDPOINT } from '@constants/env';
import { WEB_SCRAPER_EVENTS } from '@conversed/shared/src/central/constants/socket';
import type {
  WebScraperCompletedData,
  WebScraperErrorData,
  WebScraperProgressData,
} from '@conversed/shared/src/central/dto/web-scraper.dto';
import {
  reconnectAfterRetryHint,
  reconnectAfterServerDisconnect,
} from '@helpers/socketHelpers';
import { useMainSelector } from '@hooks/useMainSelector';
import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';

const { BACKEND_TO_PLATFORM } = WEB_SCRAPER_EVENTS;

export const useWebScraperSocket = (
  onProgress?: (data: WebScraperProgressData) => void,
  onCompleted?: (data: WebScraperCompletedData) => void,
  onError?: (data: WebScraperErrorData) => void,
  onConnect?: () => void,
) => {
  const {
    login: { accessToken },
  } = useMainSelector();

  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    socketRef.current = io(API_ENDPOINT, {
      ...socketIOSettings,
      path: '/sockets/platform',
      auth: {
        token: accessToken,
      },
    });
    reconnectAfterServerDisconnect(socketRef.current);
    const cancelRetryReconnect = reconnectAfterRetryHint(socketRef.current);

    socketRef.current.on(
      'connect_error',
      (
        connectError: Error & {
          data?: { 'retry-ms'?: number; reason?: string };
        },
      ) => {
        const { data } = connectError;
        const { 'retry-ms': retryMs } = data || {};

        if (typeof retryMs === 'number') {
          return console.error(
            `Websocket connection blocked, rate limited, try again in ${retryMs * 1000} seconds`,
          );
        }

        if (data?.reason) {
          return console.error(
            `Websocket connection blocked, reason ${data?.reason}`,
          );
        }

        return console.error('Unknown websocket error, connection blocked');
      },
    );

    if (onProgress) {
      socketRef.current.on(BACKEND_TO_PLATFORM.PROGRESS, onProgress);
    }

    if (onCompleted) {
      socketRef.current.on(BACKEND_TO_PLATFORM.COMPLETED, onCompleted);
    }

    if (onError) {
      socketRef.current.on(BACKEND_TO_PLATFORM.ERROR, onError);
    }

    if (onConnect) {
      socketRef.current.on('connect', onConnect);
    }

    return () => {
      cancelRetryReconnect();

      if (onProgress) {
        socketRef.current?.off(BACKEND_TO_PLATFORM.PROGRESS, onProgress);
      }

      if (onCompleted) {
        socketRef.current?.off(BACKEND_TO_PLATFORM.COMPLETED, onCompleted);
      }

      if (onError) {
        socketRef.current?.off(BACKEND_TO_PLATFORM.ERROR, onError);
      }

      if (onConnect) {
        socketRef.current?.off('connect', onConnect);
      }

      socketRef.current?.disconnect();
    };
  }, [accessToken, onProgress, onCompleted, onError, onConnect]);

  return { socket: socketRef.current };
};
